import { AgGridAngular } from '@ag-grid-community/angular';
import { ColDef } from '@ag-grid-community/core';
import { DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Component as BrComponent } from '@bloomreach/spa-sdk/';
import {
  AnalyticsService,
  DialogService,
  ModalService,
  ResponsiveService,
} from '@frk/eds-components';
import { MyliteratureService } from '@literature/literature-dashboard/services/myliterature.service';
import { LiteratureDocumentListingService } from '@literature/literature-document-listing/services/literature-document-listing.service';
import { LiteratureFilterStateManagerService } from '@literature/literature-document-listing/services/literature-filter-state-manager.service';
import { DownloadLink } from '@literature/literature-fund-document/literature-fund-document.component';
import { LiteratureListingBaseComponent } from '@literature/literature-listing-base.component';
import { fundNameGenerator, nameGenerator } from '@literature/literature-utils';
import { CartHandlerService } from '@literature/services/cart-handler.service';
import { DownloadService } from '@literature/services/download.service';
import { LiteratureAgGridHelperService } from '@literature/services/literature-ag-grid-helper.service';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { FundFilterService } from '@products/fund-listing/services/fund-filter.service';
import { IUserProfile, LoginSource } from '@services/profile.interface';
import { ProfileService } from '@services/profile.service';
import { TranslateService } from '@shared/translate/translate.service';
import {
  CartActionPerformed,
  LiteratureDocumentListingData,
  LiteratureDocumentListingState,
  LiteratureHistoryData,
  ModalId,
} from '@types';
import { createDateComparator, stringCompare } from '@utils/sort-utils';
import { Observable, Subject } from 'rxjs';
import { filter, mergeMap, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Logger } from '@utils/logger';
const logger = Logger.getLogger('MyLiteratureComponent');

@Component({
  selector: 'ft-my-literature',
  templateUrl: './my-literature.component.html',
  styleUrls: ['./my-literature.component.scss'],
  providers: [
    MyliteratureService,
    LiteratureDocumentListingService,
    DatePipe,
    LiteratureFilterStateManagerService,
    FundFilterService,
  ],
})
export class MyLiteratureComponent
  extends LiteratureListingBaseComponent
  implements OnInit, DownloadLink, OnDestroy {
  @Input() component: BrComponent;
  paramMap: Map<string, string>;
  numberOfData = 0;
  dataLoaded: boolean;
  columnDefs = [];
  isInternational: boolean;
  rowNoItems = [];
  sortByItems = [];
  sortByDropDownLabel: string;
  viewCartTitle: string;
  viewPrevOrdersTitle: string;
  isError: boolean;
  sortOn: string;
  profile$: Observable<IUserProfile>;
  data: LiteratureHistoryData;
  dateFormat: string;
  private unsubscribe$: Subject<void> = new Subject<void>();
  rowData: LiteratureDocumentListingData[] = [];
  unmodifiedData: LiteratureDocumentListingData[] = [];
  ModalId = ModalId;
  @ViewChild('literatureDocumentGrid', { static: false })
  literatureDocumentGrid: AgGridAngular;
  gridColumnApi: any;
  gridApi: any;
  href: any;

  constructor(
    private literatureDocumentListingService: LiteratureDocumentListingService,
    private literatureAgGridHelperService: LiteratureAgGridHelperService,
    private router: Router,
    protected changeDetectorRef: ChangeDetectorRef,
    protected responsiveService: ResponsiveService,
    protected translateService: TranslateService,
    protected downloadService: DownloadService,
    protected cartHandlerService: CartHandlerService,
    protected dialogService: DialogService,
    protected modalService: ModalService,
    protected formBuilder: FormBuilder,
    protected mylitratureService: MyliteratureService,
    protected profileService: ProfileService,
    protected literatureConfig: LiteratureGlobalConfigService,
    protected literatureDataService: LiteratureDataService,
    protected analyticsService?: AnalyticsService
  ) {
    super(
      responsiveService,
      changeDetectorRef,
      cartHandlerService,
      dialogService,
      downloadService,
      translateService,
      formBuilder,
      literatureConfig,
      modalService,
      profileService,
      analyticsService
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.href = this.router.url;
    this.isCartConfigured = true;
    this.isDeleteConfigured = true;
    this.dateFormat = this.literatureConfig?.dateFormat;
    this.cartHandlerService
      .getCartItems()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((item: LiteratureDocumentListingState) => {
        if (CartActionPerformed.ADDED_FROM_MY_LITERATURE === item.cartAction) {
          this.dialogData = [];
          this.dialogData = item.cartItems;
          this.ModalIdValue = ModalId.Cart_Modal;
          this.preTitle = '';
          this.modalTitle = '';
          this.openModal(ModalId.Sticky_Modal);
          this.changeDetectorRef.detectChanges();
        }
      });

    this.profile$ = this.literatureDataService.getProfileData$();
    this.literatureDataService
      .getProfileData$()
      ?.pipe(
        takeUntil(this.unsubscribe$),
        filter((profile: IUserProfile): boolean => !!profile?.isLoggedIn),
        mergeMap((profile) => {
          return this.mylitratureService.populateLiteratureHistory(
            profile.profileInfo
          );
        })
      )
      .subscribe(
        (data) => {
          this.data = data;
          this.rowData = this.mylitratureService.mapToLiteratureDocumentListingData(
            data.litHistDetail
          );
          this.unmodifiedData = [...this.rowData];
          this.isInternational = this.literatureDocumentListingService.isInternational;
          this.populateLiteratureData(this.rowData);
          this.changeDetectorRef.detectChanges();
        },
        (err) => {
          if (err?.status === 404) {
            logger.warn(
              '[populateLiteratureHistory] ' +
                err?.messaage +
                ' code: ' +
                err?.error?.error?.errorList[0]?.code
            );
          }
        }
      );

    this.sortByItems = this.createSortByDropDownItems();
    this.viewCartTitle = this.translateService.instant(
      'literature.lit-my-literature-view-cart'
    );
    this.viewPrevOrdersTitle = this.translateService.instant(
      'literature.lit-my-literature-view-previous-order'
    );
  }

  public onSorting(data: LiteratureDocumentListingData[], event) {
    this.sortOn = event.value;
    this.rowData = this.sortData(data, event.value);
    this.gridApi.setRowData(this.rowData);
  }

  private sortData(
    data: LiteratureDocumentListingData[],
    selectedValue: string
  ): LiteratureDocumentListingData[] {
    if (selectedValue === 'DEFAULT') {
      return [...this.unmodifiedData];
    } else if (selectedValue === 'DETAIL') {
      const nextUpdateDateCompare = createDateComparator(
        this.nextUpdateDateFormat
      );
      return data
        .sort((current, next) => {
          return nextUpdateDateCompare(
            current.literatureData.nextUpdateDate,
            next.literatureData.nextUpdateDate,
            null,
            null,
            false
          );
        })
        .reverse();
    } else if (selectedValue === 'NAME') {
      const nameCompare = stringCompare;
      return data.sort((current, next) => {
        return nameCompare(
          this.isInternational
            ? nameGenerator(current, this.translateService)
            : fundNameGenerator(current, this.translateService),
          this.isInternational
            ? nameGenerator(next, this.translateService)
            : fundNameGenerator(next, this.translateService),
          null,
          null,
          false
        );
      });
    } else if (selectedValue === 'PUBLICATION_DATE') {
      const dateCompare = createDateComparator(this.apiDateFormat);
      return data
        .sort((current, next) => {
          return dateCompare(
            current.literatureData.frkReferenceDate,
            next.literatureData.frkReferenceDate,
            null,
            null,
            false
          );
        })
        .reverse();
    }
  }

  private populateLiteratureData(data: LiteratureDocumentListingData[]) {
    this.columnDefs = this.getColumnDefs();
    this.rowData = data;
    this.numberOfData = data.length;
    this.changeDetectorRef.detectChanges();
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }

  private getColumnDefs(): ColDef[] {
    return this.literatureAgGridHelperService.getMyLiteratureColDef(
      this.isInternational,
      this.literatureDocumentListingService.isInvestor(),
      this.literatureConfig.navigationUrls?.litDetailUrl,
      this.dateFormat,
      this,
      CartActionPerformed.ADDED_FROM_MY_LITERATURE,
      this.literatureConfig.isCanada
    );
  }

  public deleteRow(data: any) {
    --this.numberOfData;
    this.profile$
      .pipe(
        filter((profile) => !!profile),
        mergeMap((profile) => {
          if (profile.isLoggedIn) {
            return this.mylitratureService.deleteLiteratureDocument(
              profile.profileInfo,
              data.data.literatureData.docId
            );
          }
        })
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        this.literatureDocumentGrid.api.applyTransaction({
          remove: [data.data],
        });
      });
  }
  /**
   *
   * @returns sorted items for all columns in the grid
   */
  private getSortByItemsDef(): ColDef[] {
    return [
      {
        colId: 'DEFAULT',
        headerName: this.translateService.instant(
          'literature.lit-listing-sort-most-recent'
        ),
      },
      {
        colId: 'NAME',
        headerName: this.translateService.instant(
          'literature.lit-listing-sort-alphabetical'
        ),
      },
      {
        colId: 'DETAIL',
        headerName: this.translateService.instant(
          'literature.lit-listing-sort-next-update'
        ),
      },
      {
        colId: 'PUBLICATION_DATE',
        headerName: this.translateService.instant(
          'literature.lit-listing-sort-publication-date'
        ),
      },
    ];
  }
  /**
   * sorts dropdown items
   * @returns sorted items
   */
  private createSortByDropDownItems(): ColDef[] {
    let sortByItemValues = [];
    const sortByItemsDef = this.getSortByItemsDef();
    sortByItemValues = sortByItemsDef.map((colDefItem) => {
      return {
        value: colDefItem.colId,
        label: colDefItem.headerName,
      };
    });
    return sortByItemValues;
  }
  /**
   * naviagtes to history
   * @returns path
   */
  public getNavigateToHisoryUrl(): string {
    const url = this.mylitratureService.getLink();
    return url;
  }
  /**
   * Naviagtes to cart
   * @returns path
   */
  public getNavigateToCartUrl(): string {
    return `${this.literatureConfig?.navigationUrls?.litOrderCheckoutUrl}`;
  }
  /**
   *
   * @param url url
   * @returns download url for selected doc
   */
  public getDownloadUrl(url: string): string {
    return this.literatureDocumentListingService.getDownloadUrl(url);
  }
  /**
   * Need to fix return type
   * @param recipients list of recipients
   * @param litCodes litcodes
   * @param showStatus status message to be displayed
   * @returns void
   */
  public postEmailDocs(
    recipients: string[],
    litCodes: string[],
    showStatus?: (statusMessage: string) => void
  ): void {
    return this.literatureDocumentListingService.postEmailDocs(
      recipients,
      litCodes,
      showStatus
    );
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
